import { useTramsAccounts, useCurrentTramsContext, useSelectTramsAccount } from "@/hooks/useTramsAccounts";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building2, Loader2 } from "lucide-react";

interface TramsAccountSwitcherProps {
  onAccountChange?: (tramsAccountId: number) => void;
  className?: string;
  disabled?: boolean;
}

function GemStatusBadge({ gemStatus }: { gemStatus: string | null }) {
  if (!gemStatus) return null;

  return (
    <Badge variant="secondary" className="text-xs font-medium">
      {gemStatus}
    </Badge>
  );
}

export function TramsAccountSwitcher({ onAccountChange, className, disabled }: TramsAccountSwitcherProps) {
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const { gemStatus } = useUserProfile();
  const { data: accounts, isLoading: accountsLoading } = useTramsAccounts();
  const { data: currentData, isLoading: contextLoading } = useCurrentTramsContext();
  const selectAccount = useSelectTramsAccount();

  const userDisplayName =
    impersonation.active && impersonation.targetDisplayName
      ? impersonation.targetDisplayName.trim()
      : user?.displayName?.trim() || "";

  const currentContext = currentData?.context;
  const isLoading = accountsLoading || contextLoading;

  if (isLoading) {
    return (
      <div className={`flex min-w-0 items-center gap-2 text-sm text-muted-foreground ${className ?? ""}`}>
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        <span>Loading TRAMS accounts...</span>
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return null;
  }

  if (accounts.length === 1) {
    const account = accounts[0];
    return (
      /* AR-316: this row holds an email plus two badges. Without min-w-0 +
         truncate it cannot shrink, and at 320px it pushed the whole page
         sideways (317px of content in a 288px box). */
      <div
        className={`flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1 text-sm ${className ?? ""}`}
      >
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate font-medium md:flex-none">
          {userDisplayName || account.agentName}
        </span>
        <GemStatusBadge gemStatus={gemStatus} />
        <Badge variant="outline" className="shrink-0 text-xs font-mono">
          Profile {account.profileId}
        </Badge>
      </div>
    );
  }

  const handleChange = async (value: string) => {
    const tramsAccountId = parseInt(value, 10);
    if (isNaN(tramsAccountId)) return;

    try {
      await selectAccount.mutateAsync({ tramsAccountId });
      onAccountChange?.(tramsAccountId);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className={`flex min-w-0 max-w-full items-center gap-2 ${className ?? ""}`}>
      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <Select
        value={currentContext?.tramsAccountId?.toString() ?? ""}
        onValueChange={handleChange}
        disabled={disabled || selectAccount.isPending}
      >
        {/* AR-316: was a hard w-[280px] — 280 + icon + gap + page padding
            exceeds a 320px viewport. */}
        <SelectTrigger className="h-9 w-full min-w-0 sm:w-[280px]">
          <SelectValue placeholder="Select TRAMS Account" />
        </SelectTrigger>
        <SelectContent>
          {accounts.map((account) => (
            <SelectItem key={account.id} value={account.id.toString()}>
              <div className="flex items-center gap-2">
                <span>{userDisplayName || account.agentName}</span>
                <GemStatusBadge gemStatus={gemStatus} />
                <span className="text-xs text-muted-foreground font-mono">
                  #{account.profileId}
                </span>
                {account.isDefault && (
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                    Default
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
